<template>
  <div class="modals">
    <ContactUsModal />

    <!--// Authenticated Users only modals-->
    <template v-if="$auth.loggedIn">
      <!--
       these modals cause SSR desyncs
       e.g try opening the url /war-room/posts/tab/my_feed?dynamic_modal=referral-program
      -->
      <ClientOnly>
        <HeaderReferralModal />
        <HeaderReferralHiredModal />
      </ClientOnly>
    </template>

    <!--// Guest only modals-->
    <template v-if="!$auth.loggedIn">
      <SignInModal />
      <RestorePasswordModal />
      <SetPasswordModal />
      <ClientOnly>
        <SignUpPopup />
      </ClientOnly>
    </template>
    <AnnouncementModal v-if="isWarRoomPages" />

    <!--
      That modal is general (for users with/without signup) because when user trying to sign up
      through the login/pass form, after the registration in background, modal will disappear and produce blink
    -->
    <SignUpModal />
  </div>
</template>

<script lang="ts" setup>
/**
 * @deprecated Please consider registering the component that you want exactly where you need it.
 * By placing it here you are increasing the bundle size for the entire application.
 */
import type { Route } from 'vue-router';
import { computed, useContext, useRoute, watch, onMounted, onBeforeUnmount, useRouter } from '@nuxtjs/composition-api';
import { pick } from '@/components/utils/core/object';
import { RESERVED_QUERY_PARAMS } from '~/components/common/constants';
import SignInModal from '~/components/common/SignInModal/SignInModal.vue';
import SignUpModal from '~/components/common/SignUpModal/SignUpModal.vue';
import ContactUsModal from '~/components/common/ContactUsModal.vue';
import SetPasswordModal from '~/components/common/SetPasswordModal.vue';

const SignUpPopup = () => import('~/components/common/SignUpPopup/SignUpPopup.vue');
const AnnouncementModal = () => import('~/components/common/AnnouncementModal/AnnoucementModal.vue');
const RestorePasswordModal = () => import('~/components/common/RestorePasswordModal.vue');
const HeaderReferralModal = () =>
  import('~/components/global/HeaderMenu/HeaderReferral/HeaderReferralModal/HeaderReferralModal.vue');
const HeaderReferralHiredModal = () =>
  import('~/components/global/HeaderMenu/HeaderReferral/HeaderReferralHiredModal/HeaderReferralHiredModal.vue');

type ModalEvent = { name: string; params: Record<string, { dynamic_modal: string }> };

const { $pages, $modal, $analytics, $sentryUtils } = useContext();
const router = useRouter();
const route = useRoute();

const isWarRoomPages = computed(() => route.path.startsWith($pages.POSTS_PAGE.path));

function toggle(route: Route, oldRoute?: Route) {
  const {
    query: { dynamic_modal: modal },
  } = route;

  // FIXME: we must log events not here (by changing route🤯), but in show/hide/toogle
  //        now we don't cover cases when we use $modal.show
  if (oldRoute) {
    const {
      query: { dynamic_modal: oldModal, tab },
    } = oldRoute;
    if (oldModal) {
      $modal.hide(oldModal as string, { silent: true });
      $analytics?.logEvent('global__modal__close', { name: oldModal, tab });
    }
  }

  if (modal) {
    $modal.show(modal as string, route.query);
    $analytics?.logEvent('global__modal__open', { name: modal, tab: route.query.tab });
  }
}

watch(route, toggle);

async function handleClose({ name }: ModalEvent) {
  // closing modals results in triggering nuxt middleware. this is not always desirable (e.g. when closing sign-in modal)
  if (name === route.query.dynamic_modal) {
    try {
      await router.replace({ query: pick(route.query, RESERVED_QUERY_PARAMS) });
    } catch (e) {
      // fixme: here we silence the errors for duplicated navigation or interrupted navigation
      // this is non-breaking, but must be addressed
      $sentryUtils.error(e);
    }
  }
}

onMounted(() => {
  toggle(route);
  $modal.emitter.$on('before-close', handleClose);
});

onBeforeUnmount(() => {
  $modal.emitter.$off('before-close', handleClose);
});
</script>

<style scoped lang="scss">
.modals {
  position: static;
}
</style>
